import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';

const GOLD = '#c8a04e';
const W = (a) => `rgba(255,255,255,${a})`;
const GA = (a) => `rgba(200,160,78,${a})`;

const INP = {
  width: '100%', boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10, padding: '11px 16px', color: '#fff', fontSize: 13.5,
  outline: 'none', transition: 'border-color .15s', fontFamily: 'inherit',
};

export default function ChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { navigate('/login'); return; }
      setUser(user);
    });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8 || !/[a-zA-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError('New password must be at least 8 characters with at least one letter and one number'); return;
    }
    if (newPassword !== confirmPassword) { setError('New passwords do not match'); return; }
    if (currentPassword === newPassword) { setError('New password must be different from your current password'); return; }
    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: user.email, password: currentPassword });
      if (signInError) { setError('Current password is incorrect'); setLoading(false); return; }
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#000', overflow: 'hidden' }}>
      <style>{`@keyframes cpDrop { 0% { top: -20%; } 100% { top: 110%; } }`}</style>

      {/* ── LEFT decorative panel ── */}
      <div style={{ flex: 1, position: 'relative', background: '#000', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 64px', overflow: 'hidden' }}>
        {/* Grid lines */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          {[{ left: '10%', delay: '0s' }, { left: '25%', delay: '2.2s' }, { left: '42%', delay: '4.5s' }, { left: '58%', delay: '0.8s' }, { left: '75%', delay: '3.1s' }, { left: '90%', delay: '1.6s' }].map(({ left, delay }, i) => (
            <div key={i} style={{ position: 'absolute', width: 1, height: '100%', top: 0, left, background: 'rgba(255,255,255,0.055)', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', width: '100%', height: '15vh', left: 0, top: '-20%', background: 'linear-gradient(to bottom, rgba(200,160,78,0) 0%, rgba(200,160,78,0.65) 75%, rgba(200,160,78,0.85) 100%)', animation: `cpDrop 7s ${delay} infinite cubic-bezier(0.4,0.26,0,0.97)` }} />
            </div>
          ))}
          {[0,1,2,3,4,5].map(i => (
            <div key={i} style={{ position: 'absolute', left: 0, right: 0, top: `${Math.round((i/5)*100)}%`, height: 1, background: 'rgba(255,255,255,0.04)' }} />
          ))}
        </div>
        {/* Grain */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.04, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")` }} />
        {/* Right fade */}
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(to right, transparent, #000)', zIndex: 2, pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 3 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 12px' }}>Account Security</p>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 48px', letterSpacing: '-0.02em', lineHeight: 1.2 }}>Keep your account<br/>safe & secure.</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            {[
              { label: 'Use a strong password', desc: 'At least 8 characters with letters and numbers.' },
              { label: 'Never share credentials', desc: 'Your Athena login is personal — don\'t share it.' },
              { label: 'Update regularly', desc: 'Changing your password periodically keeps you protected.' },
            ].map(({ label, desc }) => (
              <div key={label} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: GA(0.08), border: `1px solid ${GA(0.18)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 12.5, color: W(0.35), lineHeight: 1.6 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── DIVIDER ── */}
      <div style={{ width: 1, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />

      {/* ── RIGHT: Form ── */}
      <div style={{ width: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 56px', overflow: 'hidden', position: 'relative' }}>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          style={{ width: '100%', maxWidth: 400 }}
        >
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: '#fff', margin: '0 0 6px', letterSpacing: '-0.02em' }}>Change Password</h1>
            <p style={{ fontSize: 13.5, color: W(0.35), margin: 0 }}>Update your Athena account password</p>
          </div>

          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              style={{ marginBottom: 20, padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, fontSize: 12.5, color: 'rgba(239,68,68,0.9)', textAlign: 'center' }}>
              {error}
            </motion.div>
          )}

          {success ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              style={{ padding: '24px', background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.18)', borderRadius: 12, textAlign: 'center' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px', display: 'block' }}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#4ade80', margin: '0 0 6px' }}>Password changed!</p>
              <p style={{ fontSize: 12, color: 'rgba(74,222,128,0.55)', margin: 0 }}>Redirecting to dashboard…</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: W(0.3), letterSpacing: '0.1em', marginBottom: 7, textTransform: 'uppercase' }}>Current Password</label>
                <input type="password" required placeholder="••••••••" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                  style={INP} onFocus={e => e.target.style.borderColor = GA(0.4)} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
              </div>

              <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 0' }} />

              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: W(0.3), letterSpacing: '0.1em', marginBottom: 7, textTransform: 'uppercase' }}>New Password</label>
                <input type="password" required placeholder="••••••••" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  style={INP} onFocus={e => e.target.style.borderColor = GA(0.4)} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
                <p style={{ fontSize: 10, color: W(0.22), margin: '6px 0 0' }}>Min 8 characters with at least one letter and one number</p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: W(0.3), letterSpacing: '0.1em', marginBottom: 7, textTransform: 'uppercase' }}>Confirm New Password</label>
                <input type="password" required placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  style={INP} onFocus={e => e.target.style.borderColor = GA(0.4)} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'} />
              </div>

              <motion.button
                whileTap={{ scale: 0.98 }} type="submit" disabled={loading}
                style={{ marginTop: 4, padding: '13px', borderRadius: 10, background: 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)', border: `1px solid ${GA(0.28)}`, boxShadow: '0 4px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(200,160,78,0.1)', color: GOLD, fontWeight: 700, fontSize: 13.5, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1, letterSpacing: '0.01em', transition: 'background .2s, border-color .2s', fontFamily: 'inherit' }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.18) 0%, rgba(200,160,78,0.08) 55%, rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = GA(0.45); } }}
                onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.borderColor = GA(0.28); }}
              >
                {loading ? 'Updating…' : 'Change Password'}
              </motion.button>
            </form>
          )}

          <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: W(0.35), margin: 0 }}>
              Forgot your password?{' '}
              <Link to="/forgot-password" style={{ color: GOLD, fontWeight: 600, textDecoration: 'none' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.65'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                Reset it here
              </Link>
            </p>
          </div>
        </motion.div>

        <Link to="/dashboard" style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', fontSize: 12, color: W(0.25), textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, transition: 'color .15s', whiteSpace: 'nowrap' }}
          onMouseEnter={e => e.currentTarget.style.color = W(0.6)} onMouseLeave={e => e.currentTarget.style.color = W(0.25)}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}
